import React, { useEffect, useState } from "react";
import DocumentTitle from "react-document-title";
import { Layout, Card, Row, Col, Table, message } from "antd";
import { PieChart, Pie, Cell, Tooltip, Legend } from "recharts";
import { dashboardData } from "../../api/dashboard";
import dayjs from "dayjs";

const { Content } = Layout;

const Dashboard = () => {
    const [dashboard, setDashboard] = useState({});
    const [chartData, setChartData] = useState([]);
    const [tableData, setTableData] = useState([]);
    const [pieChartData, setPieChartData] = useState([]);

    useEffect(() => {
        const fetchDashboardData = async () => {
            try {
                const response = await dashboardData();
                const data = response?.data?.data || {};
                setDashboard(data);
                processChartData(data);
                processPieChartData(data);
            } catch (e) {
                console.error(e);
                message.error("There is an error in fetching the dashboard data.");
            }
        };
        fetchDashboardData();
    }, []);

    const processChartData = (data) => {
        const allData = [
            { type: "Users", records: data?.user || [] },
            { type: "Franchise", records: data?.franchise || [] },
            { type: "Dealerships", records: data?.dealership || [] },
            { type: "Investors", records: data?.investor || [] },
        ];

        const monthlyCounts = {};

        allData.forEach(({ type, records }) => {
            records.forEach(({ createdAt }) => {
                const month = dayjs(createdAt).format("YYYY-MM");
                if (!monthlyCounts[month]) {
                    monthlyCounts[month] = { month };
                }
                monthlyCounts[month][type] = (monthlyCounts[month][type] || 0) + 1;
            });
        });

        const formattedChartData = Object.values(monthlyCounts).flatMap((entry) =>
            allData.map(({ type }) => ({
                month: entry.month,
                type,
                value: entry[type] || 0,
            }))
        );

        const formattedTableData = Object.values(monthlyCounts).map((entry) => ({
            key: entry.month,
            month: entry.month,
            users: entry.Users || 0,
            franchise: entry.Franchise || 0,
            dealerships: entry.Dealerships || 0,
            investors: entry.Investors || 0,
        }));

        setChartData(formattedChartData);
        setTableData(formattedTableData);
    };

    const processPieChartData = (data) => {
        setPieChartData([
            // { name: "Users", value: data?.user?.length || 0 },
            { name: "Franchise", value: data?.franchise?.length || 0 },
            { name: "Dealerships", value: data?.dealership?.length || 0 },
            { name: "Investors", value: data?.investor?.length || 0 },
        ]);
    };

    const COLORS = ["#ff4d4f", "#40a9ff", "#52c41a", "#faad14"];

    const columns = [
        { title: "Month", dataIndex: "month", key: "month" },
        {
            title: "Users",
            dataIndex: "users",
            key: "users",
            render: (text) => <span style={{ color: "#ff4d4f" }}>{text}</span>,
        },
        {
            title: "Franchise",
            dataIndex: "franchise",
            key: "franchise",
            render: (text) => <span style={{ color: "#40a9ff" }}>{text}</span>,
        },
        {
            title: "Dealerships",
            dataIndex: "dealerships",
            key: "dealerships",
            render: (text) => <span style={{ color: "#52c41a" }}>{text}</span>,
        },
        {
            title: "Investors",
            dataIndex: "investors",
            key: "investors",
            render: (text) => <span style={{ color: "#faad14" }}>{text}</span>,
        },
    ];

    return (
        <DocumentTitle title="My Dashboard">
            <Layout
                style={{
                    height: "82vh",
                    padding: "24px",
                    background: "#f0f2f5",
                    overflowY: "scroll",
                }}
            >
                <Content>
                    <Row gutter={[16, 16]}>
                        <Col span={8}>
                            <Card title="Total Users" bordered={false} style={{ background: "#ff4d4f", color: "white" }}>
                                <h2>{dashboard?.user?.length || 0}</h2>
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card title="Total Franchise" bordered={false} style={{ background: "#40a9ff", color: "white" }}>
                                <h2>{dashboard?.franchise?.length || 0}</h2>
                            </Card>
                        </Col>
                        <Col span={8}>
                            <Card title="Total Dealerships" bordered={false} style={{ background: "#52c41a", color: "white" }}>
                                <h2>{dashboard?.dealership?.length || 0}</h2>
                            </Card>
                        </Col>
                    </Row>

                    <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
                        <Col span={8}>
                            <Card title="Total Investors" bordered={false} style={{ background: "#faad14", color: "white" }}>
                                <h2>{dashboard?.investor?.length || 0}</h2>
                            </Card>
                        </Col>
                    </Row>

                    <Row  style={{ marginTop: 16 }}>
                        
                        <Col span={24}>
                            <Card title="📊 Distribution of Users">
                                <PieChart width={400} height={400}>
                                    <Pie
                                        data={pieChartData}
                                        cx="50%"
                                        cy="50%"
                                        labelLine={false}
                                        outerRadius={120}
                                        fill="#8884d8"
                                        dataKey="value"
                                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                                    >
                                        {pieChartData.map((entry, index) => (
                                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                    <Legend />
                                </PieChart>
                            </Card>
                        </Col>
                    </Row>

                    <Card title="📊 Month-wise Growth Stats" style={{ marginTop: 16 }}>
                        <Table columns={columns} dataSource={tableData} pagination={false} bordered />
                    </Card>
                </Content>
            </Layout>
        </DocumentTitle>
    );
};

export default Dashboard;
